import { router } from "expo-router";
import { observer } from "mobx-react";
import { FormattedMessage } from "react-intl";
import { Spacer } from "~/components/Spacer";
import { Card } from "~/components/V2/Card";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useEvent } from "~/hooks/useEvent";
import { useStore } from "~/mobx/utils/useStore";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

const PractitionerPreferenceScreen = observer(() => {
  const { isStandAloneBookingFlow, clinicId, isNewUser } =
    useStandaloneBookingFlow();
  const store = useStore();

  useProgressBarStep(4);

  const navigateToNextScreen = useEvent(
    async (shouldSelectPractitioner: boolean) => {
      // unset the state in case the user navigates back to this step
      if (!shouldSelectPractitioner) {
        store.bookingStore.setPractitionerId();
      }

      const slug = shouldSelectPractitioner
        ? "select-practitioner"
        : "date-and-time";

      if (isStandAloneBookingFlow) {
        bookingDataLayerPush({
          event: "Booking_web_app",
          ga_action: shouldSelectPractitioner
            ? "Clinician preference accepted"
            : "Clinician preference declined",
        });
        router.navigate({
          pathname: `/(standalone)/booking/${slug}`,
          params: {
            clinicId,
            isNewUser: isNewUser ? "1" : "0",
          },
        });
      } else {
        router.navigate({ pathname: `/(app)/book-appointment/${slug}` });
      }
    },
  );

  return (
    <View flex colorWhite padding16>
      <View paddingVertical40 alignItemsCenter>
        <BrandText sizeH1 weightExtraBold colorBlack alignCenter>
          <FormattedMessage
            id="a5fQrr"
            defaultMessage="Do you want to choose a specific healthcare provider?"
            description="Title on the clinician select screen"
          />
        </BrandText>
      </View>
      <Card
        title={intl.formatMessage({
          id: "G8WqPE",
          defaultMessage: "No, no preference",
          description: "No preference title",
        })}
        subtitle={intl.formatMessage({
          id: "BPmCSe",
          defaultMessage: "Select this option to see all available times.",
          description: "no preference subtitle",
        })}
        onPress={() => navigateToNextScreen(false)}
      />
      <Spacer />
      <Card
        title={intl.formatMessage({
          id: "oO87cD",
          defaultMessage: "Yes, choose a healthcare provider",
          description: "Yes preference title",
        })}
        subtitle={intl.formatMessage({
          id: "HUMlR3",
          defaultMessage:
            "Select this option if you would like to meet with a specific healthcare provider. Please note that availability may be limited.",
          description: "Yes preference subtitle",
        })}
        onPress={() => navigateToNextScreen(true)}
      />
    </View>
  );
});

export const PractitionerPreferenceScreenWithBookingData = withBookingData(
  PractitionerPreferenceScreen,
  ["clinicId", "patientId", "reasonId"],
);
